import React from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  FormCheck,
  InputGroup,
  Col,
  Tooltip,
  Row,
  OverlayTrigger,
} from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { validate, ValidationError } from 'class-validator';
import moment from 'moment';
import {
  Bloodwork,
  BloodworkRange,
  PanelField,
  User,
  Client,
  UserProfile,
  WhiteBloodCellDifferentialUnit,
  WhiteBloodCellDifferentialUnitType,
  MenstrualPhase,
} from '../../models';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { MathUtils } from '../../utils/MathUtils';
import { GenericModal } from '../common/generic-modal';
import { TimeSavingFeaturesModal } from './time-saving-features-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Upload } from './upload';

const Spinner = require('react-spinkit');

type Props = {
  hideEnterTestResults?: boolean;
  selectedClient?: Client;
  currentUser: User;
  currentUserProfile: UserProfile;
  currentClients: Client[];
  bloodwork?: Bloodwork;
  bloodworkRanges: BloodworkRange[];
  bloodworkEntryStore: BloodworkEntryStore;
  refreshBloodworks: () => void;
  loadClientBloodworks?: (clientId: string) => void;
  history?: any;
  path?: any;
};

type State = {
  [key: string]: any;
  validationErrors: Map<string, string>;
  collectionDate: string;
  collectionDateForUI: Date;
  alerts: Array<any>;
  selectedClient: Client;
  clientId: string;
  disableSelectClient: boolean;
  showUpgradeModal: boolean;
  showUploadPdfUpgradeModal: boolean;
  showTimeSavingFeaturesModal: boolean;
  hideEnterTestResults: boolean;
  panelTitle: string;
};

export class BloodworkEntry extends React.Component<Props, State> {
  selectedClient: Client = this.props.selectedClient
    ? this.props.selectedClient
    : new Client();
  clientId: string = this.props.selectedClient
    ? this.props.selectedClient.id
    : undefined;

  state: State = {
    validationErrors: new Map<string, string>(),
    collectionDate: undefined,
    collectionDateForUI: undefined,
    alerts: [],
    selectedClient: this.selectedClient,
    clientId: this.clientId,
    disableSelectClient: false,
    wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType.PERCENT,
    showUpgradeModal: false,
    showUploadPdfUpgradeModal: false,
    showTimeSavingFeaturesModal: false,
    hideEnterTestResults: this.props.hideEnterTestResults,
    panelTitle:
      this.props.bloodwork && this.props.bloodwork.isNew()
        ? 'Add Test Results'
        : 'Edit Test Results',
  };

  async componentDidMount() {
    console.log('BloodworkEntry....componentDidMount.....................');
    // this.props.bloodworkEntryStore.setCurrentBloodwork(new Bloodwork());
    const showTimeSavingFeaturesModal =
      await this.displayTimeSavingFeaturesModal();
    if (showTimeSavingFeaturesModal) {
      this.setState({
        showTimeSavingFeaturesModal,
      });
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    const bloodworkData: any = {};
    const { history, hideEnterTestResults } = nextProps;
    let bloodwork = new Bloodwork(nextProps.bloodwork);
    this.scrollTop();
    this.handleNumberOfTestsLimit();

    // setup needed if we are editing bloodwork
    if (bloodwork && !bloodwork.isNew()) {
      console.log('We have bloodwork and its not new.....');

      // Check to see if we came thru the "add" route
      if (this.isAddMode(history)) {
        for (let key of Object.keys(bloodwork)) {
          bloodworkData[key] = '';
        }
        this.setState({
          ...bloodworkData,
          hideEnterTestResults: false,
          panelTitle: 'Add Test Results',
          wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType.PERCENT,
        });
        return;
      }

      const collectionDateForUI = moment(bloodwork.collectionDate).toDate();
      bloodwork.convertWBCDifferentials(bloodwork.wbcDifferentialUnits);

      for (let key of Object.keys(bloodwork)) {
        bloodworkData[key] = bloodwork[key];
      }

      this.setState({
        ...bloodworkData,
        panelTitle: 'Edit Test Results',
        selectedClient: bloodwork.client,
        disableSelectClient: true,
        hideEnterTestResults,
        collectionDateForUI: collectionDateForUI,
      });
    } else {
      console.log('inside else in componentWillReceiveProps....');
      this.setState({
        hideEnterTestResults,
        panelTitle: 'Add Test Results',
        wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType.PERCENT,
      });
    }
  }

  isAddMode(history: any) {
    return (
      history &&
      history.location &&
      history.location.pathname === '/bloodwork/add'
    );
  }

  handleNumberOfTestsLimit() {
    const { currentUserProfile } = this.props;
    if (currentUserProfile && !currentUserProfile.canAddTest()) {
      this.setState({ showUpgradeModal: true });
    }
  }

  handleUploadPdfLimit() {
    const { currentUserProfile } = this.props;
    if (currentUserProfile && !currentUserProfile.canUploadPdf()) {
      this.setState({ showUploadPdfUpgradeModal: true });
    }
  }

  handleInputChange = (event: any) => {
    // console.log(event ? event.target : 'event was falsey...');
    const fieldId = event.target.name;
    let value = event.target.value;

    // Convert empty string to undefined so our automatic calculations work correctly
    if (value.trim().length === 0) {
      value = undefined;
    }

    // If the wbcDifferentialUnits selection changes then clear out wbc differencial inputs
    if (fieldId === 'wbcDifferentialUnits') {
      this.setState({
        [fieldId]: value,
        neutrophils: '',
        lymphocytes: '',
        monocytes: '',
        eosinophils: '',
        basophils: '',
      });
    } else {
      this.setState({
        [fieldId]: value,
      });
    }

    if (fieldId === 'clientId') {
      this.handleClientChange(event.target.value);
    }

    this.clearAlerts();
  };

  handleClientChange = (clientId: string) => {
    const client = Client.findClient(clientId, this.props.currentClients);
    this.setState({
      selectedClient: client || new Client(),
    });
  };

  handleOnBlur = (event: any) => {
    this.handleCalculatedRatioFields(event.target.name);
    this.handleGlobulinCalculation(event.target.name);
    this.handleMeanCorpuscularVolumeCalculation(event.target.name);
    this.handleMeanCorpuscularHGBCalculation(event.target.name);
    this.handleMCHConcentrationCalculation(event.target.name);
    this.handleBilirubinCalculation(event.target.name);
    this.handleSaturationCalculation(event.target.name);
    this.handleTestosteroneBioavailableCalculation(event.target.name);
    this.handleLessThanCalculation(event.target.name);
  };

  handleCalculatedRatioFields(fieldId: string) {
    if (fieldId === 'bun' || fieldId === 'creatinine') {
      this.handlePopulateRatio('bun', 'creatinine', 'bunCreatinineRatio');
    } else if (fieldId === 'albumin' || fieldId === 'globulin') {
      this.handlePopulateRatio('albumin', 'globulin', 'albuminGlobulinRatio');
    } else if (fieldId === 'cholesterol' || fieldId === 'hdl') {
      this.handlePopulateRatio('cholesterol', 'hdl', 'cholesterolHDLRatio');
    } else if (fieldId === 'triglycerides' || fieldId === 'hdl') {
      this.handlePopulateRatio('triglycerides', 'hdl', 'triglyceridesHDLRatio');
    } else if (
      fieldId === 'apolipoproteinB' ||
      fieldId === 'apolipoproteinA1'
    ) {
      this.handlePopulateRatio(
        'apolipoproteinB',
        'apolipoproteinA1',
        'apoBapoA1Ratio',
      );
    }
  }

  handlePopulateRatio(
    numerator: string,
    denominator: string,
    ratioField: string,
  ) {
    const numeratorNbr = Number(this.state[numerator]);
    const denominatorNbr = Number(this.state[denominator]);
    let ratio;
    if (!isNaN(numeratorNbr) && !isNaN(denominatorNbr)) {
      ratio = MathUtils.round(numeratorNbr / denominatorNbr, 2);
      if (isNaN(ratio)) {
        ratio = '';
      }
      this.setState({
        [ratioField]: ratio,
      });
    }
  }

  handleGlobulinCalculation(fieldId: string) {
    if (fieldId === 'protein' || fieldId === 'albumin') {
      const protein = Number(this.state.protein);
      const albumin = Number(this.state.albumin);
      if (!isNaN(protein) && !isNaN(albumin)) {
        const globulin = protein - albumin;
        this.setState({
          globulin: MathUtils.round(globulin, 2),
        });
      }
    }
  }

  handleMeanCorpuscularVolumeCalculation(fieldId: string) {
    if (fieldId === 'hematocrit' || fieldId === 'redBloodCells') {
      const hct = Number(this.state.hematocrit);
      const rbc = Number(this.state.redBloodCells);
      if (!isNaN(hct) && !isNaN(rbc)) {
        //  MCV = HCT x 10 / RBC
        const mcv = (hct * 10) / rbc;
        this.setState({
          meanCorpuscularVolume: MathUtils.round(mcv, 2),
        });
      }
    }
  }

  handleMeanCorpuscularHGBCalculation(fieldId: string) {
    if (fieldId === 'hemoglobin' || fieldId === 'redBloodCells') {
      const hgb = Number(this.state.hemoglobin);
      const rbc = Number(this.state.redBloodCells);
      if (!isNaN(hgb) && !isNaN(rbc)) {
        // MCH = HGB x 10 / RBC
        const mch = (hgb * 10) / rbc;
        this.setState({
          meanCorpuscularHGB: MathUtils.round(mch, 2),
        });
      }
    }
  }

  handleMCHConcentrationCalculation(fieldId: string) {
    if (fieldId === 'hemoglobin' || fieldId === 'hematocrit') {
      const hgb = Number(this.state.hemoglobin);
      const hct = Number(this.state.hematocrit);
      if (!isNaN(hgb) && !isNaN(hct)) {
        // MCHC = HGB x 100 / HCT
        const mchc = (hgb * 100) / hct;
        this.setState({
          mchConcentration: MathUtils.round(mchc, 2),
        });
      }
    }
  }

  handleBilirubinCalculation(fieldId: string) {
    // total = direct + indirect
    // direct = total - indirect
    // indirect = total - direct
    if (
      fieldId === 'bilirubin' ||
      fieldId === 'bilirubinDirect' ||
      fieldId === 'bilirubinIndirect'
    ) {
      const total = Number(
        this.state.bilirubin === '' ? undefined : this.state.bilirubin,
      );
      let direct = Number(
        this.state.bilirubinDirect === ''
          ? undefined
          : this.state.bilirubinDirect,
      );
      let indirect = Number(
        this.state.bilirubinIndirect === ''
          ? undefined
          : this.state.bilirubinIndirect,
      );
      if (!isNaN(total) && !isNaN(direct) && isNaN(indirect)) {
        indirect = total - direct;
        this.setState({
          bilirubinIndirect: MathUtils.round(indirect, 2),
        });
      } else if (!isNaN(total) && !isNaN(indirect) && isNaN(direct)) {
        direct = total - indirect;
        this.setState({
          bilirubinDirect: MathUtils.round(direct, 2),
        });
      }
    }
  }

  handleSaturationCalculation(fieldId: string) {
    if (fieldId === 'iron' || fieldId === 'tibc') {
      const iron = Number(this.state.iron);
      const tibc = Number(this.state.tibc);
      if (!isNaN(iron) && !isNaN(tibc)) {
        //  % saturation = ( iron / TIBC ) * 100
        const saturation = (iron / tibc) * 100;
        this.setState({
          saturation: MathUtils.round(saturation, 1),
        });
      }
    }
  }

  handleTestosteroneBioavailableCalculation(fieldId: string) {
    if (
      fieldId === 'testosteroneBioavailable' ||
      fieldId === 'testosteroneTotal'
    ) {
      const bioavailable = Number(this.state.testosteroneBioavailable);
      const total = Number(this.state.testosteroneTotal);
      if (!isNaN(bioavailable) && !isNaN(total)) {
        const bioavailablePercentage = (bioavailable / total) * 100;
        this.setState({
          testosteroneBioavailablePercent: MathUtils.round(
            bioavailablePercentage,
            1,
          ),
        });
      }
    }
  }

  handleLessThanCalculation(fieldId: string) {
    if (fieldId === 'tpoAb' && this.state.tpoAb === '<9') {
      this.setState({ tpoAb: 8 });
    }

    if (fieldId === 'tpoAb' && this.state.tpoAb === '<12') {
      this.setState({ tpoAb: 11.9 });
    }

    if (fieldId === 'tpoAb' && this.state.tpoAb === '<28') {
      this.setState({ tpoAb: 27.9 });
    }

    if (fieldId === 'tpoAb' && this.state.tpoAb === '<0.3') {
      this.setState({ tpoAb: 0.2 });
    }

    if (fieldId === 'tpoAb' && this.state.tpoAb === '<1') {
      this.setState({ tpoAb: 0.9 });
    }

    if (fieldId === 'tgAb' && ['<1', '<1.0', '≤1'].includes(this.state.tgAb)) {
      this.setState({ tgAb: 0.9 });
    }

    if (fieldId === 'tgAb' && ['<15', '<15.000'].includes(this.state.tgAb)) {
      this.setState({ tgAb: 14.99 });
    }

    if (fieldId === 'tgAb' && ['>2250', '>2250.0'].includes(this.state.tgAb)) {
      this.setState({ tgAb: 2251 });
    }

    if (fieldId === 'estroneE1' && this.state.estroneE1 === '<6') {
      this.setState({ estroneE1: 5.9 });
    }

    if (fieldId === 'estriolE3' && this.state.estriolE3 === '<0.10') {
      this.setState({ estriolE3: 0.09 });
    }

        if (fieldId === 'estriolE3' && this.state.estriolE3 === '<0.2') {
      this.setState({ estriolE3: 0.19 });
    }

    if (fieldId === 'bilirubin' && this.state.bilirubin === '<0.2') {
      this.setState({ bilirubin: 0.19 });
    }

    if (
      fieldId === 'bilirubinDirect' && this.state.bilirubinDirect === '<0.2') {
      this.setState({ bilirubinDirect: 0.19 });
    }

    if (fieldId === 'insulin' && this.state.insulin === '<3') {
      this.setState({ insuln: 2.9 });
    }

    if (
      fieldId === 'testosteroneFreeDirect' && this.state.testosteroneFreeDirect === '<0.2') {
      this.setState({ testosteroneFreeDirect: 0.19 });
    }

    if (
      fieldId === 'testosteroneTotal' && this.state.testosteroneTotal === '<2.5') {
      this.setState({ testosteroneTotal: 2.4 });
    }

    if (fieldId === 'lipoproteinA' && this.state.lipoproteinA === '<8.4') {
      this.setState({ lipoproteinA: 8.3 });
    }

    if (fieldId === 'lipoproteinA' && this.state.lipoproteinA === '<10') {
      this.setState({ lipoproteinA: 9.9 });
    }

    if (fieldId === 'lipoproteinA' && this.state.lipoproteinA === '<19.6') {
      this.setState({ lipoproteinA: 19.5 });
    }

    if (fieldId === 'folateSerum' && this.state.folateSerum === '>20.0') {
      this.setState({ folateSerum: 21 });
    }

    if (fieldId === 'folateSerum' && this.state.folateSerum === '>24.0') {
      this.setState({ folateSerum: 25 });
    }

    if (fieldId === 'folateRBC' && this.state.folateRBC === '>1396') {
      this.setState({ folateRBC: 1397 });
    }

    if (fieldId === 'folateRBC' && this.state.folateRBC === '>1325') {
      this.setState({ folateRBC: 1326 });
    }

    if (fieldId === 'bileAcids' && this.state.bileAcids === '<1.0') {
      this.setState({ bileAcids: 0.9 });
    }

    if (fieldId === 'ldl' && this.state.ldl === '<100') {
      this.setState({ ldl: 99 });
    }

    if (fieldId === 'estradiolE2' && this.state.estradiolE2 === '<15') {
      this.setState({ estradiolE2: 14.9 });
    }

    if (fieldId === 'estradiolE2' && this.state.estradiolE2 === '<10') {
      this.setState({ estradiolE2: 9.9 });
    }

    if (fieldId === 'estradiolE2' && this.state.estradiolE2 === '<5.0') {
      this.setState({ estradiolE2: 4.9 });
    }

    if (fieldId === 'estradiolE2' && this.state.estradiolE2 === '<30') {
      this.setState({ estradiolE2: 29.9 });
    }

    if (fieldId === 'pregnenolone' && this.state.pregnenolone === '<5') {
      this.setState({ pregnenolone: 4 });
    }

    if (fieldId === 'progesterone' && this.state.progesterone === '<0.5') {
      this.setState({ progesterone: 0.4 });
    }

    if (fieldId === 'progesterone' && this.state.progesterone === '<0.1') {
      this.setState({ progesterone: 0.09 });
    }
    
    if (fieldId === 'progesterone' && this.state.progesterone === '<0.05') {
      this.setState({ progesterone: 0.04 });
    }

    if (fieldId === 'lh' && this.state.lh === '<0.05') {
      this.setState({ lh: 0.04 });
    }

    if (fieldId === 'hsCRP' && this.state.hsCRP === '<0.2') {
      this.setState({ hsCRP: 0.1 });
    }

    if (fieldId === 'hsCRP' && this.state.hsCRP === '<0.15') {
      this.setState({ hsCRP: 0.14 });
    }

    if (fieldId === 'eGFR' && this.state.eGFR === '>90') {
      this.setState({ eGFR: 91 });
    }

    if (fieldId === 'eGFRforAA' && this.state.eGFRforAA === '>90') {
      this.setState({ eGFRforAA: 91 });
    }

    if (fieldId === 'dDimer' && this.state.dDimer === '<0.20') {
      this.setState({ dDimer: 0.1 });
    }
  }

  handleCollectionDateChange = (value: any) => {
    const startOfDay = moment(value).startOf('day').toISOString();
    this.setState({
      collectionDate: startOfDay,
      collectionDateForUI: value,
    });
    this.clearAlerts();
  };

  handleSubmit = (event: any) => {
    event.preventDefault();

    if (!this.props.currentUserProfile.canAddTest()) {
      this.handleNumberOfTestsLimit();
      return;
    }

    let validationErrors = new Map<string, string>();
    let bloodwork: Bloodwork = new Bloodwork();
    bloodwork.practitionerId = this.props.currentUser.subject;

    for (let key of Object.keys(this.state)) {
      if (
        key !== 'validationErrors' &&
        key !== 'alerts' &&
        key !== 'selectedClient' &&
        key !== 'collectionDateForUI' &&
        key !== 'hideEnterTestResults' &&
        key !== 'panelTitle' &&
        key !== 'disableSelectClient' &&
        key !== 'showUpgradeModal' &&
        key !== 'showUploadPdfUpgradeModal' &&
        key !== 'showTimeSavingFeaturesModal'
      ) {
        if (this.state[key] === '') {
          // Remove numeric attributes that have empty string
          // DynamoDB will NOT accept empty string....Stupid, right!
          // nulls will be given the value of true....weirdness!
          bloodwork[key] = undefined;
        } else {
          const nbr: number = Number(this.state[key]);
          if (isNaN(nbr)) {
            bloodwork[key] = this.state[key];
          } else {
            bloodwork[key] = nbr;
          }
        }
      }
    }
    // console.log(bloodwork);
    this.validate(bloodwork, validationErrors);
  };

  validate(bloodwork: Bloodwork, validationErrors: Map<string, string>) {
    // console.log(bloodwork);

    // Special check for bilirubin
    if (!bloodwork.isBilirubinValid()) {
      // Use a fake key here so the messages doesn't have the field name in it
      validationErrors.set(
        'fakeBilirubinKey1',
        'Bilirubin, Direct and Bilirubin, Indirect must not be greater than Bilirubin, Total.',
      );
      validationErrors.set(
        'fakeBilirubinKey2',
        'Click the Clear Bilirubin button next to Bilirubin, Total.',
      );
    }

    // Special check for collection date and client DOB
    if (bloodwork.collectionDate && bloodwork.clientId) {
      const { currentClients } = this.props;
      const client = Client.findClient(bloodwork.clientId, currentClients);

      if (!client.isBornOnOrBefore(bloodwork.collectionDate)) {
        validationErrors.set(
          'fakeCollectionDate',
          "Collection Date must be the same or after the client's Date of Birth.",
        );
      }
    }

    // Special check to see if menstrualPhase is required and if we should clear it
    if (this.isMenstrualPhaseRequired() && !bloodwork.menstrualPhase) {
      validationErrors.set(
        'menstrualPhase',
        'is required for premenopausal female clients when Estradiol, E2 is provided.',
      );
    } else if (!this.showMenstrualPhase()) {
      // If we arent supposed to show it then clear it
      bloodwork.menstrualPhase = undefined;
    }

    if (validationErrors.size > 0) {
      this.setState({
        validationErrors,
      });

      this.scrollToAddTestResults();
      return;
    }

    validate(bloodwork).then(errors => {
      // errors is an array of validation errors
      if (errors.length > 0) {
        for (let error of errors) {
          this.displayErrorMessage(error, validationErrors);
        }

        this.setState({
          validationErrors: validationErrors,
        });

        this.scrollToAddTestResults();
      } else {
        this.setState({
          validationErrors: new Map<string, string>(),
        });
        // Set WBC Differentials if not entered as percentages
        const wbcDifferentialUnits = parseInt(
          this.state.wbcDifferentialUnits,
          10,
        );
        bloodwork.convertWBCDifferentialsIfNecessary(wbcDifferentialUnits);
        // console.log('bloodwork:', bloodwork);
        this.saveBloodwork(bloodwork);
      }
    });
  }

  saveBloodwork(bloodwork: Bloodwork) {
    const {
      refreshBloodworks,
      loadClientBloodworks,
      bloodworkEntryStore,
      currentClients,
    } = this.props;

    // Populate client on bloodwork if new
    if (bloodwork.isNew()) {
      bloodwork.client = Client.findClient(bloodwork.clientId, currentClients);
    }

    bloodworkEntryStore
      .saveBloodwork(bloodwork)
      .then(savedBloodwork => {
        // console.log(savedBloodwork);
        this.addAlert(
          { type: 'success', text: 'Successfully saved test result!' },
          false,
          5000,
        );
        // Load the client bloodworks since we could be editing a bloodwork on the analyze bloodworks page
        if (loadClientBloodworks && bloodwork.clientId) {
          loadClientBloodworks(bloodwork.clientId);
        }
        refreshBloodworks();
        this.clearForm(false);
        this.handleNumberOfTestsLimit();
        bloodworkEntryStore.resetCurrentBloodwork();
        bloodworkEntryStore.setHideEnterTestResults(true);
      })
      .catch(error => {
        console.error(error);
        this.addAlert({ type: 'danger', text: 'Failed to add test result.' });
      });
  }

  clearForm = (clearAlerts?: boolean) => {
    const bloodwork = {};
    Object.keys(this.state).forEach(key => {
      if (
        key !== 'validationErrors' &&
        key !== 'collectionDate' &&
        key !== 'alerts' &&
        key !== 'selectedClient' &&
        key !== 'showUpgradeModal' &&
        key !== 'showUploadPdfUpgradeModal' &&
        key !== 'showTimeSavingFeaturesModal'
      ) {
        bloodwork[key] = '';
      }
    });

    this.setState({
      ...bloodwork,
      panelTitle: 'Add Test Results',
      validationErrors: new Map<string, string>(),
      collectionDate: undefined,
      collectionDateForUI: undefined,
      selectedClient: new Client(),
      wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType.PERCENT,
    });

    if (clearAlerts) {
      this.clearAlerts();
    }
  };

  clearAlerts = () => {
    this.setState({
      alerts: [],
    });
  };

  displayErrorMessage(
    error: ValidationError,
    validationErrors: Map<string, string>,
  ) {
    for (let constraint of Object.keys(error.constraints)) {
      validationErrors.set(error.property, error.constraints[constraint]);
    }
  }

  renderErrorListItems(validationErrors: Map<string, string>) {
    let listItems: JSX.Element[] = [];
    validationErrors.forEach((value: string, fieldName: string) => {
      if (validationErrors.get(fieldName)) {
        const fieldLabel = this.findFieldLabel(fieldName);
        listItems.push(
          <li key={fieldName + '-error'}>
            {fieldLabel} {validationErrors.get(fieldName)}
          </li>,
        );
      }
    });

    return listItems;
  }

  findFieldLabel(attribute: string): string {
    const panelField: PanelField = PanelField.ALL_FIELDS.find(field => {
      return field.id === attribute;
    });
    return panelField ? panelField.label : '';
  }

  renderValidationErrors(validationErrors: Map<string, string>) {
    return (
      <div className="panel">
        <div className="validation-errors">
          <ul>{this.renderErrorListItems(validationErrors)}</ul>
        </div>
      </div>
    );
  }

  renderAlerts() {
    // console.log('renderAlerts called....');
    const alerts = this.state.alerts.map((alert: any, index: number) => {
      return this.renderAlert(alert, index);
    });

    return <div>{alerts}</div>;
  }

  renderAlert(alert: any, index: number) {
    return (
      <Alert key={index} variant={alert.type} dismissible>
        {alert.text}
      </Alert>
    );
  }

  renderErrors() {
    const { validationErrors } = this.state;
    return validationErrors.size > 0 ? (
      this.renderValidationErrors(validationErrors)
    ) : (
      <div />
    );
  }

  addAlert = (
    alert: any,
    keepCurrentAlerts: boolean = false,
    autoClose?: number,
  ) => {
    // console.log('add alert called....');
    const alerts = keepCurrentAlerts ? [...this.state.alerts, alert] : [alert];
    this.setState({
      alerts,
    });

    if (autoClose) {
      setTimeout(() => {
        this.clearAlerts();
      }, autoClose);
    }
  };

  createFieldElements = (field: PanelField) => {
    if (field.id === 'collectionDate') {
      return this.collectionDate(field);
    }
    if (field.id === 'clientId') {
      return this.selectClient(field);
    }
    if (field.id === 'wbcDifferentialUnits') {
      return this.wbcDifferentialUnits(field);
    }
    if (field.id === 'menstrualPhase') {
      return this.menstrualPhase(field);
    }
    return this.input(field);
  };

  collectionDate(field: PanelField) {
    return (
      // TODO: Look into using a customInput here so that validation styling is applied.
      <DatePicker
        selected={this.state.collectionDateForUI}
        onChange={this.handleCollectionDateChange}
        maxDate={new Date()}
        peekNextMonth
        showMonthDropdown
        showYearDropdown
        todayButton
        dropdownMode="select"
        dateFormat=" MM/dd/yyyy"
        placeholderText=" MM/DD/YYYY"
        popperPlacement="bottom"
        popperModifiers={{
          flip: {
            behavior: ['bottom'], // don't allow it to flip to be above
          },
          preventOverflow: {
            enabled: false, // tell it not to try to stay within the view (this prevents the popper from covering the element you clicked)
          },
          hide: {
            enabled: false, // turn off since needs preventOverflow to be enabled
          },
        }}
      />
    );
  }

  selectClient(field: PanelField) {
    console.log(
      'rendering selectClient......disableSelectClient: ' +
        this.state.disableSelectClient,
    );
    const { currentClients } = this.props;

    if (currentClients === undefined) {
      return <Spinner name="ball-clip-rotate" color="#337ab7" fadeIn="none" />;
    }

    if (currentClients.length === 0) {
      return (
        <LinkContainer exact to="/client/add">
          <Button>Add Client</Button>
        </LinkContainer>
      );
    }

    return (
      <Form.Control
        as="select"
        name={field.id}
        placeholder={field.placeholder}
        value={this.state[field.id]}
        onChange={this.handleInputChange}
        disabled={this.state.disableSelectClient}
        isInvalid={!!this.state.validationErrors.get(field.id)}
      >
        <option value="">{field.placeholder}</option>
        {currentClients.map((client: Client, index: number) => {
          return (
            <option key={index} value={client.id}>
              {client.lastName}, {client.firstName}
            </option>
          );
        })}
      </Form.Control>
    );
  }

  wbcDifferentialUnits(field: PanelField) {
    let wbcDifferentialUnits = parseInt(this.state.wbcDifferentialUnits, 10);
    // Make sure we default a value if none present
    if (!wbcDifferentialUnits) {
      wbcDifferentialUnits = WhiteBloodCellDifferentialUnitType.ABSOLUTE_KCELLS;
    }
    return (
      <InputGroup>
        <FormCheck
          inline
          id="unit2"
          name={field.id}
          value={WhiteBloodCellDifferentialUnitType.ABSOLUTE_KCELLS}
          label={WhiteBloodCellDifferentialUnit.ABSOLUTE_KCELLS}
          type="radio"
          onChange={this.handleInputChange}
          checked={
            wbcDifferentialUnits ===
            WhiteBloodCellDifferentialUnitType.ABSOLUTE_KCELLS
          }
        ></FormCheck>
        &nbsp;&nbsp;&nbsp;
        <FormCheck
          inline
          id="unit3"
          name={field.id}
          value={WhiteBloodCellDifferentialUnitType.ABSOLUTE_CELLS}
          label={WhiteBloodCellDifferentialUnit.ABSOLUTE_CELLS}
          type="radio"
          onChange={this.handleInputChange}
          checked={
            wbcDifferentialUnits ===
            WhiteBloodCellDifferentialUnitType.ABSOLUTE_CELLS
          }
        ></FormCheck>
        &nbsp;&nbsp;&nbsp;
        <FormCheck
          id="unit1"
          name={field.id}
          value={WhiteBloodCellDifferentialUnitType.PERCENT}
          inline
          label={WhiteBloodCellDifferentialUnit.PERCENT}
          type="radio"
          onChange={this.handleInputChange}
          checked={
            wbcDifferentialUnits === WhiteBloodCellDifferentialUnitType.PERCENT
          }
        ></FormCheck>
      </InputGroup>
    );
  }

  menstrualPhase(field: PanelField) {
    return (
      <Form.Control
        as="select"
        name="menstrualPhase"
        value={this.state.menstrualPhase}
        onChange={this.handleInputChange}
        isInvalid={!!this.state.validationErrors.get(field.id)}
      >
        <option value="">{field.placeholder}</option>
        <option value={MenstrualPhase.FOLLICULAR}>
          {MenstrualPhase.FOLLICULAR}
        </option>
        <option value={MenstrualPhase.OVULATION}>
          {MenstrualPhase.OVULATION}
        </option>
        <option value={MenstrualPhase.LUTEAL}>{MenstrualPhase.LUTEAL}</option>
        <option value={MenstrualPhase.NONE}>
          {MenstrualPhase.NONE} (children's ranges)
        </option>
      </Form.Control>
    );
  }

  input(field: PanelField) {
    const { validationErrors } = this.state;

    return (
      <InputGroup>
        <Form.Control
          name={field.id}
          onChange={this.handleInputChange}
          onBlur={this.handleOnBlur}
          placeholder={this.placeholder(field)}
          type="text"
          value={this.state[field.id]}
          isInvalid={!!validationErrors.get(field.id)}
        />
        {this.units(field)}
      </InputGroup>
    );
  }

  units(field: PanelField) {
    let units = field.units;
    const wbcDifferentialUnits = parseInt(this.state.wbcDifferentialUnits, 10);
    if (field.isWBCDifferential) {
      units = field.wbcUnits(wbcDifferentialUnits);
    }
    return units ? (
      <InputGroup.Append>
        <InputGroup.Text>{units}</InputGroup.Text>
      </InputGroup.Append>
    ) : (
      ''
    );
  }

  placeholder(field: PanelField): string {
    if (field.isWBCDifferential) {
      const wbcDifferentialUnits = parseInt(
        this.state.wbcDifferentialUnits,
        10,
      );
      return field.wbcPlaceholder(wbcDifferentialUnits);
    } else if (field.placeholder) {
      return field.placeholder;
    } else {
      const { bloodworkRanges } = this.props;
      const { selectedClient, collectionDate, menstrualPhase } = this.state;
      const bloodwork = new Bloodwork({
        client: selectedClient,
        collectionDate: collectionDate || moment().startOf('day').toISOString(),
        menstrualPhase,
      });

      const range = BloodworkRange.findRange(
        bloodworkRanges,
        field.id,
        bloodwork,
      );

      if (range) {
        // console.log('placeholder.................................');
        // console.log(range);
        return range.placeholder(field.getPlaceholderPrecision());
      }
    }
    return undefined;
  }

  clearBilirubin() {
    this.setState({
      bilirubin: '',
      bilirubinDirect: '',
      bilirubinIndirect: '',
    });
  }

  addAdditionalInfo(field: PanelField) {
    if (field.id === 'bilirubin' || field.id === 'bilirubinDirect') {
      const tooltip = (
        <Tooltip id="bilirubin-tooltip">
          <div>
            Click this button to clear the values for Bilirubin, Total, Direct,
            and Indirect if a mistake was made in entering Bilirubin, Direct or
            Indirect.
          </div>
          <div>
            Re-enter the value for Bilirubin, Total first, then enter a value
            for either Direct or Indirect and the other will be automatically
            calculated.
          </div>
        </Tooltip>
      );
      return (
        <OverlayTrigger overlay={tooltip}>
          <Button
            type="button"
            onClick={() => {
              this.clearBilirubin();
            }}
          >
            Clear Bilirubin
          </Button>
        </OverlayTrigger>
      );
    }
    return null;
  }

  isMenstrualPhaseRequired() {
    const { estradiolE2 } = this.state;
    return estradiolE2 && this.showMenstrualPhase();
  }

  showMenstrualPhase() {
    const { selectedClient, collectionDate } = this.state;
    if (selectedClient && collectionDate) {
      const age = selectedClient.ageAsOf(collectionDate);
      return selectedClient.isPremenopausal() && age > 10;
    }
    return false;
  }

  formGroup(field: PanelField) {
    const className = field.isFormCheck ? 'labsmarts-form-check-label' : '';
    // Only show menstrual phase if necessary
    if (field.id === 'menstrualPhase' && !this.showMenstrualPhase()) {
      return null;
    }
    return (
      <Form.Group
        as={Row}
        key={field.id + '-group'}
        controlId={field.id + '-group'}
      >
        <Form.Label column sm className={className}>
          {field.label}
        </Form.Label>
        <Col sm>{this.createFieldElements(field)}</Col>
        <Col sm>{this.addAdditionalInfo(field)}</Col>
      </Form.Group>
    );
  }

  scrollToAddTestResults() {
    const element = document.getElementById('add-test-results-panel');
    if (element) {
      element.scrollIntoView();
    }
  }

  showTimeSavingFeaturesModal = () => {
    this.setState({
      showTimeSavingFeaturesModal: true,
    });
  };

  async displayTimeSavingFeaturesModal(): Promise<boolean> {
    const { currentUserProfile } = this.props;
    if (currentUserProfile) {
      const hasSeenTimeSavingFeaturesModal = await localStorage.getItem(
        `${currentUserProfile.id}-hasSeenTimeSavingFeaturesModal`,
      );
      return hasSeenTimeSavingFeaturesModal !== 'true';
    } else {
      return Promise.resolve(false);
    }
  }

  hasSeenTimeSavingFeaturesModal = (): void => {
    // console.log('hasSeenTimeSavingFeaturesModal invoked....');
    const { currentUserProfile } = this.props;
    localStorage.setItem(
      `${currentUserProfile.id}-hasSeenTimeSavingFeaturesModal`,
      'true',
    );
    this.setState({
      showTimeSavingFeaturesModal: false,
    });
  };

  scrollTop() {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }

  toggleEnterTestResults() {
    this.setState({
      hideEnterTestResults: !this.state.hideEnterTestResults,
    });
  }

  render() {
    const {
      panelTitle,
      showUpgradeModal,
      showUploadPdfUpgradeModal,
      showTimeSavingFeaturesModal,
      hideEnterTestResults,
    } = this.state;
    const addTestResultsInfoTooltip = (
      <Tooltip id="modal-tooltip">
        Click this icon to learn more about time saving features.
      </Tooltip>
    );
    const hideEnterTestResultsIconName = hideEnterTestResults
      ? 'chevron-down'
      : 'chevron-up';

    return (
      <div>
        {this.renderAlerts()}

        <GenericModal
          show={showUpgradeModal}
          title="Maximum Number of Test Results Reached"
          text="You have reached the maximum number of test results allowed."
          link="https://labsmarts.com/pricing"
          linkText="Free user - click here to upgrade."
          additionalLink="https://labsmarts.com/my-account"
          additionalLinkText="Paid user - click here to upgrade"
          onHide={() => this.setState({ showUpgradeModal: false })}
        />

        <GenericModal
          show={showUploadPdfUpgradeModal}
          title="Upload Test Results Feature Not Available"
          text="Upload Test Results feature is not available."
          link="https://labsmarts.com/upload_test_results_add_on"
          linkText="Click here to upgrade."
          onHide={() => this.setState({ showUploadPdfUpgradeModal: false })}
        />

        <TimeSavingFeaturesModal
          show={showTimeSavingFeaturesModal}
          onHide={this.hasSeenTimeSavingFeaturesModal}
        />

        <Card id="add-test-results-panel">
          <Card.Header onClick={() => this.toggleEnterTestResults()}>
            <FontAwesomeIcon icon={hideEnterTestResultsIconName} />
            &nbsp;&nbsp;{panelTitle}
            <OverlayTrigger overlay={addTestResultsInfoTooltip}>
              <FontAwesomeIcon
                icon="info-circle"
                className="float-right"
                onClick={this.showTimeSavingFeaturesModal}
              />
            </OverlayTrigger>
          </Card.Header>

          {hideEnterTestResults ? null : (
            <Card.Body>
              {this.renderErrors()}
              <br />
              <Form noValidate>
                {PanelField.GENERAL_FIELDS.map((field: any, index: number) => {
                  return field.isNotEditable ? null : this.formGroup(field);
                })}
                {this.props.currentUserProfile?.canSeeUploadTestResults() && (
                  <Upload
                    isUploadAvailable={this.props.currentUserProfile?.canUploadPdf()}
                    showModal={() => this.handleUploadPdfLimit()}
                    setState={obj => {
                      this.setState(prev => ({ ...prev, ...obj }));
                      setTimeout(() =>
                        Object.keys(obj).forEach(key =>
                          this.handleOnBlur({ target: { name: key } }),
                        ),
                      );
                    }}
                    clearForm={() => this.clearForm(true)}
                  />
                )}
              </Form>
              <Card>
                <Card.Header>
                  Complete Blood Count (CBC) w/ Differentials &amp; Platelets
                </Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.CBC_FIELDS.map((field: any, index: number) => {
                      return field.isNotEditable ? null : this.formGroup(field);
                    })}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Comprehensive Metabolic Panel (CMP)</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.CMP_FIELDS.map((field: any, index: number) => {
                      return field.isNotEditable ? null : this.formGroup(field);
                    })}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Lipid Panel</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.LIPID_FIELDS.map(
                      (field: any, index: number) => {
                        return field.isNotEditable
                          ? null
                          : this.formGroup(field);
                      },
                    )}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Iron Panel</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.IRON_FIELDS.map((field: any, index: number) => {
                      return field.isNotEditable ? null : this.formGroup(field);
                    })}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Thyroid Panel</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.THYROID_FIELDS.map(
                      (field: any, index: number) => {
                        return field.isNotEditable
                          ? null
                          : this.formGroup(field);
                      },
                    )}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Additional Markers</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.ADDITIONAL_FIELDS.map(
                      (field: any, index: number) => {
                        return field.isNotEditable
                          ? null
                          : this.formGroup(field);
                      },
                    )}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Hormones Panel</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.SEX_HORMONE_FIELDS.map(
                      (field: any, index: number) => {
                        return field.isNotEditable
                          ? null
                          : this.formGroup(field);
                      },
                    )}
                  </Form>
                </Card.Body>
              </Card>
              <Button
                id="submitButton"
                type="submit"
                onClick={this.handleSubmit}
                variant="primary"
              >
                Save
              </Button>
              &nbsp;&nbsp;
              <Button
                id="clearButton"
                type="button"
                variant="outline-primary"
                onClick={() => this.clearForm(true)}
                disabled={!this.isAddMode(this.props.history)}
              >
                Clear
              </Button>
            </Card.Body>
          )}
        </Card>
      </div>
    );
  }
}
